import React, { useState } from "react";
import { toast } from "sonner";
import { FileText } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { useNavigate } from "react-router-dom";

export default function QuoteRequestModal({ trigger, prefillItems = [] }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [phone, setPhone] = useState(user?.phone || "");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!user) {
      navigate("/login");
      return;
    }
    if (!message.trim()) {
      toast.error("Please describe what you need a quote for.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/quotes", { message, contact_phone: phone, items: prefillItems });
      toast.success("Quote request submitted. Our team will respond shortly.");
      setOpen(false);
      setMessage("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" className="rounded-full" data-testid="open-quote-modal">
            <FileText className="h-4 w-4 mr-1.5" /> Request a quote
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading">Request a custom wholesale quote</DialogTitle>
          <DialogDescription>
            For large volumes or custom packaging, tell us your requirement and we'll get back with special pricing.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label htmlFor="quote-msg">Requirement</Label>
            <Textarea
              id="quote-msg"
              data-testid="quote-message-input"
              placeholder="e.g. 2,000 units of Basmati Rice 25kg, delivered monthly to Mumbai..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              className="mt-1.5"
            />
          </div>
          <div>
            <Label htmlFor="quote-phone">Contact phone</Label>
            <Input
              id="quote-phone"
              data-testid="quote-phone-input"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+91 ..."
              className="mt-1.5"
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            onClick={submit}
            disabled={loading}
            className="w-full bg-slate-900 hover:bg-black text-white rounded-full"
            data-testid="submit-quote-btn"
          >
            {loading ? "Submitting..." : "Submit quote request"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
